import styled from 'styled-components';
import { THEME } from '@/common/styles/theme';

export const AuthOverlay = styled.div`
	position: fixed;
	inset: 0;
	z-index: 7000;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 24px;
	background: rgba(3, 1, 5, 0.58);
	backdrop-filter: blur(24px) saturate(1.08);

	@media (max-width: 760px) {
		padding: 0;
		align-items: flex-start;
		justify-content: flex-start;
	}
`;

export const AuthCard = styled.article`
	position: relative;
	width: min(860px, calc(100vw - 40px));
	max-height: min(520px, calc(100vh - 40px));
	overflow: hidden;
	display: grid;
	grid-template-columns: minmax(300px, 0.88fr) minmax(320px, 1.12fr);
	align-items: stretch;
	border: 1px solid rgba(255, 255, 255, 0.14);
	border-radius: 18px;
	background:
		radial-gradient(circle at 74% 0%, rgba(255, 77, 242, 0.13), transparent 34%),
		rgba(18, 7, 20, 0.96);
	box-shadow:
		0 34px 110px rgba(0, 0, 0, 0.72),
		0 0 36px rgba(255, 77, 242, 0.12);

	@media (max-width: 760px) {
		width: 100%;
		max-height: 100dvh;
		min-height: 100dvh;
		border-radius: 0;
		border-left: 0;
		border-right: 0;
		border-top: 0;
		grid-template-columns: 1fr;
		grid-template-rows: auto minmax(0, 1fr);
		overflow: hidden;
	}
`;

export const AuthVisual = styled.section`
	position: relative;
	min-height: 420px;
	background: #09050c;
	overflow: hidden;

	&::after {
		content: '';
		position: absolute;
		inset: 0;
		background:
			linear-gradient(180deg, transparent 42%, rgba(0, 0, 0, 0.82) 100%),
			linear-gradient(90deg, transparent 68%, rgba(18, 7, 20, 0.68) 100%);
	}

	img {
		width: 100%;
		height: 100%;
		min-height: 420px;
		object-fit: cover;
		object-position: center top;
		display: block;
	}

	@media (max-width: 760px) {
		min-height: unset;
		height: 132px;

		img {
			width: 100%;
			height: 132px;
			min-height: unset;
			max-height: 132px;
		}

		&::after {
			background: linear-gradient(180deg, transparent 20%, rgba(0, 0, 0, 0.84) 100%);
		}
	}
`;

export const AuthVisualCopy = styled.div`
	position: absolute;
	left: 24px;
	right: 24px;
	bottom: 24px;
	z-index: 1;

	h2 {
		margin: 0 0 10px;
		color: ${THEME.gold};
		font-family: ${THEME.fontDisplay};
		font-size: 1.45rem;
		line-height: 1;
		text-shadow: 0 0 14px rgba(255, 211, 107, 0.34);
	}

	p {
		margin: 0;
		max-width: 240px;
		color: rgba(255, 255, 255, 0.94);
		font-size: 0.82rem;
		line-height: 1.35;
	}

	@media (max-width: 760px) {
		left: 16px;
		right: 16px;
		bottom: 12px;

		h2 {
			margin-bottom: 6px;
			font-size: 1.15rem;
		}

		p {
			font-size: 0.75rem;
			line-height: 1.3;
		}
	}
`;

export const AuthContent = styled.section`
	position: relative;
	display: flex;
	flex-direction: column;
	min-width: 0;
	height: 100%;
	min-height: 420px;

	@media (max-width: 760px) {
		min-height: unset;
		height: auto;
		min-height: 0;
		overflow-y: auto;
		-webkit-overflow-scrolling: touch;
	}
`;

export const AuthClose = styled.button`
	position: absolute;
	top: 14px;
	right: 14px;
	z-index: 2;
	width: 34px;
	height: 34px;
	border: 0;
	border-radius: 50%;
	color: rgba(255, 255, 255, 0.66);
	background: transparent;
	font-size: 1.6rem;
	line-height: 1;
	cursor: pointer;

	&:hover {
		color: #fff;
		background: rgba(255, 255, 255, 0.06);
	}

	@media (max-width: 760px) {
		top: max(14px, env(safe-area-inset-top, 0px));
		right: max(14px, env(safe-area-inset-right, 0px));
	}
`;

export const AuthHeader = styled.header`
	flex-shrink: 0;
	padding: 22px 54px 16px 26px;
	border-bottom: 1px solid rgba(255, 255, 255, 0.12);

	@media (max-width: 760px) {
		padding: 20px 48px 14px 20px;
	}
`;

export const AuthTitle = styled.h2`
	margin: 0 0 6px;
	font-family: ${THEME.fontDisplay};
	font-size: 1.2rem;
	line-height: 1.15;
	color: ${THEME.text};
`;

export const AuthHint = styled.p`
	margin: 0;
	font-size: 0.82rem;
	line-height: 1.45;
	color: rgba(255, 255, 255, 0.72);
`;

export const AuthMain = styled.div`
	flex: 1;
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	min-height: 0;
	padding: 22px 26px 20px;

	@media (max-width: 760px) {
		padding: 18px 20px calc(18px + env(safe-area-inset-bottom, 0px));
	}
`;

export const AuthSwitchRow = styled.p`
	margin: 16px 0 0;
	text-align: center;
	font-size: 0.82rem;
	line-height: 1.45;
	color: rgba(255, 255, 255, 0.62);
`;

export const AuthSwitchLink = styled.button`
	border: 0;
	background: transparent;
	padding: 0;
	margin-left: 6px;
	color: ${THEME.pink};
	font-family: ${THEME.fontApp};
	font-size: 0.82rem;
	font-weight: 800;
	cursor: pointer;

	&:hover {
		color: ${THEME.text};
		text-decoration: underline;
		text-underline-offset: 3px;
	}
`;

export const AuthFormRoot = styled.div`
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0;
`;

export const AuthFormEl = styled.form`
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0;
`;

export const AuthFormFields = styled.div`
	display: flex;
	flex-direction: column;
	gap: 14px;
`;

export const AuthFormBottom = styled.div`
	margin-top: auto;
	padding-top: 20px;
`;

export const AuthStepHint = styled.p`
	margin: 0 0 4px;
	font-size: 0.82rem;
	line-height: 1.4;
	color: ${THEME.muted};
`;

export const AuthError = styled.p`
	margin: 0 0 14px;
	padding: 10px 12px;
	border-radius: 10px;
	border: 1px solid rgba(255, 77, 242, 0.28);
	background: rgba(255, 77, 242, 0.08);
	color: #ffb8f5;
	font-size: 0.82rem;
	line-height: 1.35;
`;

export const AuthSuccess = styled.p`
	margin: 0 0 14px;
	padding: 12px 14px;
	border-radius: 10px;
	border: 1px solid rgba(84, 214, 161, 0.35);
	background: rgba(84, 214, 161, 0.1);
	color: #9ef0c8;
	font-size: 0.84rem;
	line-height: 1.45;
`;

export const AuthField = styled.div`
	display: flex;
	flex-direction: column;
	gap: 7px;
`;

export const AuthLabel = styled.label`
	font-size: 0.74rem;
	font-weight: 700;
	letter-spacing: 0.05em;
	text-transform: uppercase;
	color: rgba(255, 255, 255, 0.55);
`;

export const AuthInput = styled.input`
	width: 100%;
	height: 44px;
	padding: 0 14px;
	border: 1px solid rgba(255, 255, 255, 0.14);
	border-radius: 11px;
	color: ${THEME.text};
	font-family: ${THEME.fontApp};
	font-size: 0.9rem;
	background: rgba(255, 255, 255, 0.04);
	transition:
		border-color 160ms ease,
		box-shadow 160ms ease;

	&::placeholder {
		color: rgba(255, 255, 255, 0.34);
	}

	&:focus {
		outline: none;
		border-color: rgba(255, 77, 242, 0.5);
		box-shadow: 0 0 0 3px rgba(255, 77, 242, 0.12);
	}

	&:disabled {
		opacity: 0.55;
		cursor: not-allowed;
	}
`;

export const AuthPasswordWrap = styled.div`
	position: relative;
	width: 100%;
`;

export const AuthPasswordInput = styled(AuthInput)`
	padding-right: 44px;
`;

export const AuthPasswordToggle = styled.button`
	position: absolute;
	top: 50%;
	right: 10px;
	transform: translateY(-50%);
	width: 32px;
	height: 32px;
	border: 0;
	border-radius: 8px;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	color: rgba(255, 255, 255, 0.55);
	background: transparent;
	cursor: pointer;

	&:hover:not(:disabled) {
		color: #fff;
		background: rgba(255, 255, 255, 0.06);
	}

	&:disabled {
		opacity: 0.55;
		cursor: not-allowed;
	}
`;

export const AuthSubmit = styled.button`
	width: 100%;
	height: 46px;
	margin-top: 0;
	border: 0;
	border-radius: 999px;
	padding: 0 20px;
	font-family: ${THEME.fontApp};
	font-size: 0.9rem;
	font-weight: 800;
	color: #fff;
	background: ${(props) =>
		props.$success
			? `linear-gradient(135deg, #2f9f72, ${THEME.green})`
			: THEME.gradientPrimary};
	box-shadow: ${(props) =>
		props.$success
			? '0 10px 28px rgba(84, 214, 161, 0.3)'
			: '0 10px 28px rgba(176, 38, 255, 0.28)'};
	cursor: pointer;
	transition:
		transform 160ms ease,
		box-shadow 160ms ease,
		opacity 160ms ease,
		background 220ms ease;

	&:hover:not(:disabled) {
		transform: translateY(-1px);
		box-shadow: ${(props) =>
			props.$success
				? '0 14px 32px rgba(84, 214, 161, 0.36)'
				: '0 14px 32px rgba(255, 77, 242, 0.34)'};
	}

	&:disabled {
		opacity: ${(props) => (props.$success ? 1 : 0.6)};
		cursor: not-allowed;
	}
`;

export const AuthSubmitContent = styled.span`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
`;

export const AuthSubmitSpinner = styled.span`
	width: 16px;
	height: 16px;
	border: 2px solid rgba(255, 255, 255, 0.35);
	border-top-color: #fff;
	border-radius: 50%;
	animation: authSubmitSpin 0.8s linear infinite;

	@keyframes authSubmitSpin {
		to {
			transform: rotate(360deg);
		}
	}
`;

export const AuthLinks = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 8px 16px;
	justify-content: flex-start;
	margin-top: 10px;
`;

export const AuthLinkButton = styled.button`
	border: 0;
	background: transparent;
	padding: 0;
	color: ${THEME.soft};
	font-family: ${THEME.fontApp};
	font-size: 0.8rem;
	text-decoration: underline;
	text-underline-offset: 3px;
	cursor: pointer;

	&:hover {
		color: ${THEME.text};
	}
`;

export const ResetOverlay = styled.div`
	position: fixed;
	inset: 0;
	z-index: 7100;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 24px;
	background: rgba(3, 1, 5, 0.72);
	backdrop-filter: blur(20px) saturate(1.08);
`;

export const ResetCard = styled.div`
	position: relative;
	width: min(100%, 400px);
	padding: 24px 24px 22px;
	border: 1px solid rgba(255, 255, 255, 0.14);
	border-radius: 18px;
	background:
		radial-gradient(circle at 80% 0%, rgba(255, 77, 242, 0.12), transparent 38%),
		rgba(18, 7, 20, 0.98);
	box-shadow:
		0 28px 88px rgba(0, 0, 0, 0.72),
		0 0 28px rgba(255, 77, 242, 0.1);
`;

export const ResetClose = styled.button`
	position: absolute;
	top: 14px;
	right: 14px;
	width: 34px;
	height: 34px;
	border: 0;
	border-radius: 50%;
	color: rgba(255, 255, 255, 0.66);
	background: transparent;
	font-size: 1.6rem;
	line-height: 1;
	cursor: pointer;

	&:hover {
		color: #fff;
		background: rgba(255, 255, 255, 0.06);
	}
`;

export const ResetHeader = styled.header`
	padding-right: 36px;
	margin-bottom: 18px;

	h3 {
		margin: 0 0 6px;
		font-family: ${THEME.fontDisplay};
		font-size: 1.15rem;
		line-height: 1.2;
		color: ${THEME.text};
	}

	p {
		margin: 0;
		font-size: 0.82rem;
		line-height: 1.45;
		color: rgba(255, 255, 255, 0.7);
	}
`;

export const ResetForm = styled.form`
	display: flex;
	flex-direction: column;
`;

export const ResetSubmit = styled(AuthSubmit)`
	margin-top: 18px;
`;

export const ResetBack = styled.button`
	margin-top: 14px;
	border: 0;
	background: transparent;
	padding: 0;
	align-self: center;
	color: ${THEME.soft};
	font-family: ${THEME.fontApp};
	font-size: 0.82rem;
	text-decoration: underline;
	text-underline-offset: 3px;
	cursor: pointer;

	&:hover {
		color: ${THEME.text};
	}
`;
